import { AxiosRequestConfig } from 'axios'
import {
  MunMeh as Data,
  MunMehCollection as Collection,
  MunMehCuratorForm,
  MunMehCuratorFormCollection,
  MunMehDocument,
  MunMehDocumentCollection,
  MunMehExpert,
  MunMehExpertCollection,
  MunMehForm as Form,
  MunMehFormProgress, MunMehFormUserActionHistory,
  MunMehFormUserActionHistoryCollection,
  MunMehFormUserActionHistoryIndicator,
  MunMehIndicator,
  MunMehIndicatorComment,
  MunMehIndicatorCommentCollection, MunMehReport,
  MunMehStatistics,
  MunMehStatisticsCollection,
} from '@/typings/api/MunMeh'
import {
  File as FileData,
} from '@/typings/api/File'
import { BaseApiParams, BaseApiResponse as BR } from '@/typings/common'

import { BaseLkApiAuth } from '@/api/BaseApiAuth'

type CountResponse = {
  count: number;
}

export type MarkStatus = 'in_progress' | 'done' | 'not_started' | null

export type IndicatorValue = {
  value: number;
  indicator_id: number;
  indicator_value_id: number;
  status: MarkStatus;
}

export type IndicatorValueCollection = {
  data: {
    marks: IndicatorValue[];
    first_level_one_expert_marks: IndicatorValue[];
    first_level_two_expert_marks: IndicatorValue[];
    second_level_expert_marks: IndicatorValue[];
  };
}

export class LkMunMehApi extends BaseLkApiAuth<Data, Collection> {
  protected readonly endpoint = 'munmeh';

  public getFormActionsSortedHistoryList<P extends BaseApiParams>(
    formId: Form['id'], data?: Record<string, unknown>, rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = `/forms/${formId}/sorted_actions/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehFormUserActionHistory, MunMehFormUserActionHistoryCollection>('POST', url, { data, ...config, params })
  }

  public getFormLoggedIndicators<P extends BaseApiParams>(formId: Form['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/changed_indicators_list/`
    const [url, params] = this.getUrl(path)
    return this.request<number[]>('GET', url, { params, ...config })
  }

  public finish<P extends BaseApiParams>(formId: Form['id'],
    data?: Record<string, unknown>, rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/finish/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request('POST', url, { data, ...config, params })
  }

  public getMunMehReport<P extends BaseApiParams>(munMehId: Data['id'], data?: Record<string, unknown>, rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/${munMehId}/generate-report/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehReport>('POST', url, { data, ...config, params })
  }

  public getMenMehFormReport<P extends BaseApiParams>(formId: Form['id'], data?: Record<string, unknown>, rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/generate-report/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehReport>('POST', url, { data, ...config, params })
  }

  public getFormActionsHistoryList<P extends BaseApiParams>(formId: Form['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/actions/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehFormUserActionHistoryIndicator, MunMehFormUserActionHistoryIndicator[]>('GET', url, { params, ...config })
  }

  public saveMunMehIndicatorComments<P extends BaseApiParams>(formId: Form['id'], indicatorId: MunMehIndicator['id'],
    data?: Record<string, unknown>, rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/indicator/${indicatorId}/comment/save/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request('POST', url, { data, ...config, params })
  }

  public getMunMehIndicatorComments<P extends BaseApiParams>(formId: Form['id'], indicatorId: MunMehIndicator['id'],
    rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/indicator/${indicatorId}/comments/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehIndicatorComment, MunMehIndicatorCommentCollection>('GET', url, { params, ...config })
  }

  public getMunMehFormsProgress<P extends BaseApiParams>(munMehId: Data['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/${munMehId}/forms/progress/`
    const [url, params] = this.getUrl(path)
    return this.request<MunMehFormProgress>('GET', url, { params, ...config })
  }

  public getFormById<P extends BaseApiParams>(formId: Form['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}`
    const [url, params] = this.getUrl(path)
    return this.request<Form>('GET', url, { params, ...config })
  }

  public openFormById(formId: Form['id'], data?: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/open/`
    const [url, params] = this.getUrl(path)
    return this.request<Form>('POST', url, { data, params, ...config })
  }

  public validateMarks<P extends BaseApiParams>(formId: Data['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/marks/validate/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<BR<IndicatorValue[]>>('GET', url, { params, ...config })
  }

  public updateExperts(formId: number, data?: Record<string, unknown>, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/experts/update/`
    const [url, params] = this.getUrl(path)
    return this.request('POST', url, { data, ...config, params })
  }

  public getExperts<P extends BaseApiParams>(munMehId: Data['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/${munMehId}/experts/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehExpert, MunMehExpertCollection>('GET', url, { params, ...config })
  }

  public getExpertsForms<P extends BaseApiParams>(munMehId: Data['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/${munMehId}/experts/forms/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehCuratorForm, MunMehCuratorFormCollection>('GET', url, { params, ...config })
  }

  public getCuratorForms<P extends BaseApiParams>(munMehId: Data['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/${munMehId}/curators/forms/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehCuratorForm, MunMehCuratorFormCollection>('GET', url, { params, ...config })
  }

  public getStatistics<P extends BaseApiParams>(munMehId: Data['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = '/statistics'
    const [url, params] = this.getUrl(path, { ...rawParams, mun_meh_id: munMehId })
    return this.request<MunMehStatistics, MunMehStatisticsCollection>('GET', url, { params, ...config })
  }

  public saveAnyLevelMarks<P extends BaseApiParams>(formId: Form['id'], parentLevelId: Form['id'], data: Record<string, unknown>,
    rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/parent_level/${parentLevelId}/marks/save/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request('POST', url, { data, ...config, params })
  }

  public getAnyLevelMarks<P extends BaseApiParams>(formId: Form['id'], parentLevelId: Form['id'], rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/parent_level/${parentLevelId}/marks/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<IndicatorValue, IndicatorValueCollection>('GET', url, { params, ...config })
  }

  public getThirdLevelDocuments<P extends BaseApiParams>(formId: Form['id'], thirdLevelId: MunMehIndicator['id'],
    rawParams?: P, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/third_level/${thirdLevelId}/data/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<MunMehDocument, MunMehDocumentCollection>('GET', url, { params, ...config })
  }

  public saveThirdLevelDocuments(formId: Form['id'], thirdLevelId: MunMehIndicator['id'], data: Record<string, unknown>,
    config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/third_level/${thirdLevelId}/data/save/`
    const [url] = this.getUrl(path, data)
    return this.request('POST', url, { data, ...config })
  }

  public createForm(munMehId: Data['id'], config?: AxiosRequestConfig) {
    const path = `/${munMehId}/form/create/`
    const [url] = this.getUrl(path)
    return this.request('POST', url, { ...config })
  }

  public submit(formId: Form['id'], config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/submit/`
    const [url] = this.getUrl(path)
    return this.request('POST', url, config)
  }

  public mark(formId: Form['id'], config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/mark/`
    const [url] = this.getUrl(path)
    return this.request('POST', url, config)
  }

  public count<P extends BaseApiParams>(rawParams?: P, config?: AxiosRequestConfig) {
    const path = '/count/'
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<CountResponse>('GET', url, { params, ...config })
  }

  public getMunMeh<P extends BaseApiParams>(
    publicationId: Data['id'], rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = '/'
    const [url, params] = this.getUrl(path, { publication_id: publicationId })
    return this.request<null, { data: NonNullable<Collection> }>('GET', url, { params, ...config })
  }

  public downloadXlsx(formId: Form['id']) {
    const path = `/forms/${formId}/xlsx-download/`
    const [url] = this.getUrl(path)
    return url
  }

  public uploadXlsx(formId: Form['id'], file: File, config?: AxiosRequestConfig) {
    const path = `/forms/${formId}/xlsx-upload/`
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, BR<FileData['raw_file']>>('POST', url, { data, ...config })
  }
}
